<script setup lang="ts">
import { computed } from 'vue';

import IconCheckCircle from '~/assets/icons/IconCheckCircle.vue';
import IconFileText from '~/assets/icons/IconFileText.vue';
import InfoGridItem from '~/components/shared/InfoGridItem.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import { GridItem } from '~/types/grid';
import money from '~/utils/money';

interface Props {
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();

const completedAt = computed(() => props.financing.section_statuses?.simulation?.completed_at || undefined);
const submittedAt = computed(() => props.financing.section_statuses?.simulation?.submitted_at || undefined);

const formatPower = (power: null | number | undefined): string => {
  if (power == null) return '-';
  if (power >= 1000) {
    return `${(power / 1000).toFixed(2)} kW`;
  }
  return `${power} W`;
};

const gridItems = computed<GridItem[]>(() => [
  {
    isTag: true,
    label: 'Valor Financiado',
    tagColor: 'success',
    value: money.formatBRL(props.financing.financed_value),
  },
  {
    label: 'Potência do Sistema',
    value: formatPower(props.financing.system_power),
  },
  {
    label: 'Tipo de Projeto',
    value: props.financing.person_type === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica',
  },
  {
    isTag: true,
    label: 'Combo',
    tagColor: 'success',
    value: props.financing.is_combo ? 'Sim' : 'Não',
  },
]);
</script>

<template>
  <div class="simulation-step">
    <div class="mb-4">
      <div class="row g-3">
        <div class="status-card-col">
          <StatusCard
            title="Submetido em"
            :datetime="submittedAt"
            :icon="IconFileText"
            type="informative"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="completedAt"
            :icon="IconCheckCircle"
            type="positive"
          />
        </div>
      </div>
    </div>

    <div class="mt-4">
      <div class="datagrid">
        <InfoGridItem
          v-for="item in gridItems"
          :key="item.label"
          :label="item.label"
          :is-tag="item.isTag"
          :tag-color="item.tagColor"
          :value="item.value"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.simulation-step {
  .status-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }
  }

  .datagrid {
    display: grid;
    gap: 0.75rem;
  }
}
</style>
